import { useQueryCache } from "@pinia/colada";
import { TAGS_QUERY_KEY } from "~/queries/tags";
import AuthAPI from "~/services/auth";
import { checkAccessTokenExpiration } from "~/utils/token";

const useUserStore = defineStore("user", {
  state: () => ({
    user: null as null | IModelUser,
    devices: [] as IModelDevice[],
    followed: {} as Record<IModelFollowedItem["id"], IModelFollowedItem>,
    selected: {} as Record<string, boolean>,
    token: null as null | string,
    userTokenData: null as null | IModelUserTokenData,
    creditCard: null as null | IModelCreditCard,
    creditCardToken: null as null | string,
    ppvTickets: [] as IModelCommerce[],
    pendingAccessTokenRefresh: null as null | Promise<void>,
  }),
  persist: [
    {
      // Critical auth data - use cookies for SSR compatibility
      key: "user-auth",
      storage: piniaPluginPersistedstate.cookies(),
      pick: ["token", "userTokenData"],
    },
    {
      // Non-critical user data - use localStorage (client-only)
      key: "user-data",
      storage: piniaPluginPersistedstate.localStorage(),
      pick: ["user", "followed", "devices"],
    },
  ],
  getters: {
    isAuthenticated: (state) => {
      if (!state.userTokenData?.token || !state.user)
        return false;

      const isExpired = checkAccessTokenExpiration(
        state.userTokenData.tokenExpirationTime,
        state.userTokenData.tokenUpdateTime,
      );

      return !isExpired;
    },

    followedIdentitiesIds: (state) => {
      return Object.values(state.followed).reduce((acc: string[], item) => {
        if (item.identitiesId && item.identitiesId.length > 0) {
          item.identitiesId.forEach((id) => {
            if (!acc.includes(id))
              acc.push(id);
          });
        }
        return acc;
      }, []);
    },

    followedItems: (state) => {
      return Object.values(state.followed);
    },
  },

  actions: {
    logout(options: { revokeToken: boolean } = { revokeToken: true }): void {
      const segment = useSegment();
      const queryClient = useQueryCache();

      if (options.revokeToken)
        this.userTokenData?.token && AuthAPI.logout(this.userTokenData.token);

      // Clear user data from store
      this.token = null;
      this.userTokenData = null;
      this.user = null;
      this.creditCard = null;
      this.creditCardToken = null;
      this.selected = {};
      this.followed = {};
      this.devices = [];
      this.ppvTickets = [];

      queryClient.invalidateQueries({
        key: TAGS_QUERY_KEY.root,
      });

      segment.reset();
    },
    refreshAccessToken(): Promise<void> {
      return AuthAPI.refreshAccessToken();
    },
  },
});

export default useUserStore;

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
